import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOptimizedImageUrl } from '../lib/images';

export default function DistributionPage() {
  useEffect(() => {
    document.title = 'Meta SoMe Postkalender & Profilopsætning - Health4media.dk';
    window.scrollTo(0, 0);
  }, []);

  const channels = [
    {
      name: 'Instagram Reels',
      tagline: 'Opbygning af stærkt lokalt kendskab og patientloyalitet',
      desc: 'Ideel til sundhedsklinikker, hvor visuel appel, menneskeligt nærvær og lokal forankring er i centrum.',
      icon: 'fa-instagram'
    },
    {
      name: 'TikTok',
      tagline: 'Maksimal organisk rækkevidde og hurtig viral spredning',
      desc: 'Perfekt til pædagogisk sundhedsviden, mytedræbere og afmystificering af almindelige behandlingsformer.',
      icon: 'fa-tiktok'
    },
    {
      name: 'YouTube Shorts & YouTube',
      tagline: 'Langsigtet søgbarhed, når patienter googler symptomer',
      desc: 'Sundhedssøgende bruger YouTube som søgemaskine. Videoerne forbliver relevante og søgbare i årevis.',
      icon: 'fa-youtube'
    },
    {
      name: 'LinkedIn & Facebook',
      tagline: 'B2B-samarbejder, henvisninger og lokalsamfund',
      desc: 'Styrker jeres faglige omdømme over for henvisende læger, sundhedsforsikringer og lokale netværk.',
      icon: 'fa-linkedin-in'
    }
  ];

  const distributionSteps = [
    {
      number: '01',
      title: 'Geografisk lokal målretning',
      desc: 'Vi sikrer, at videoerne eksponeres for de borgere og patienter, der bor i klinikkens reelle optageområde.'
    },
    {
      number: '02',
      title: 'Fast udgivelsesrytme',
      desc: 'Algoritmerne belønner kontinuitet. Vi sørger for en fast kadence på 2–4 ugentlige videoer uden spidsbelastning for jer.'
    },
    {
      number: '03',
      title: 'Optimeret tekst, tags & hooks',
      desc: 'Hver video ledsages af faglige beskrivelser, relevante søgeord og klare call-to-actions, der guider seeren mod booking.'
    },
    {
      number: '04',
      title: 'Løbende analyse & justering',
      desc: 'Vi overvåger hvilke emner der skaber flest henvendelser, og justerer strategien måned for måned.'
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HERO SECTION */}
      <section className="bg-slate-900 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-4 sm:space-y-6">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Målrettet distribution, der omsætter videoer til patienter
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed font-normal">
                Selv de bedste videoer skaber kun værdi, hvis de ses af de rigtige mennesker. Vi tager hånd om udgivelse, platformstilpasning og lokal synlighed.
              </p>
              <div className="pt-2 sm:pt-4 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 sm:px-7 sm:py-3.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm sm:text-base transition shadow-lg shadow-sky-950/40"
                >
                  <span>Book en samtale</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 rounded-full bg-transparent hover:bg-transparent hover:-translate-y-1 transform text-white font-medium text-xs sm:text-sm transition-all duration-200 border border-white"
                >
                  <span>Se opnåede resultater</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden aspect-4/3 shadow-2xl border border-slate-700 bg-slate-800 max-h-[260px] sm:max-h-none">
                <img
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/gallery-shortform-editing.png", { width: 700, height: 525, fit: 'cover' })}
                  alt="Meta SoMe postkalender og profilopsætning til sundhedsklinikker"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* STRATEGY & STEPS */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Vores distributionsproces
            </h2>
            <p className="text-base text-slate-600 mt-3 leading-relaxed">
              En struktureret model, der opbygger stabil og vedvarende synlighed uden spild af budget.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {distributionSteps.map((step, idx) => (
              <div
                key={idx}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-bold text-sky-600 uppercase tracking-wider block mb-3">
                    Trin {step.number}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900">
                    {step.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CONVERSION & MEASURABILITY */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Fra videoeksponering til konkret booking i klinikken
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                Synlighed har kun værdi, når det fører til handling. Vi opbygger en klar sti fra det øjeblik, patienten ser jeres faglige video, til de booker en konsultation på klinikkens hjemmeside.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Klar call-to-action i hver video</h4>
                    <p className="text-xs sm:text-sm text-slate-600">Patienterne guides naturligt mod klinikkens profil og direkte bookinglink.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Lokal rækkevidde uden spild</h4>
                    <p className="text-xs sm:text-sm text-slate-600">Fokus på de postnumre og byer, der er relevante for jeres fysiske praksis.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Månedlig opfølgning og rapportering</h4>
                    <p className="text-xs sm:text-sm text-slate-600">I får et overskueligt overblik over visninger, interaktioner og performance.</p>
                  </div>
                </div>
              </div>

            </div>

            <div className="bg-slate-900 rounded-3xl p-8 sm:p-10 text-white border border-slate-800 space-y-6">
              <h3 className="text-2xl font-bold text-white tracking-tight">
                I ejer altid jeres egne kanaler og data
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Alt indhold udgives direkte på klinikkens egne officielle profiler (eller klargøres til jeres interne team). I bevarer 100% ejerskab over følgere, rækkevidde og videomateriale.
              </p>
              <div className="pt-4 border-t border-slate-800">
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition"
                >
                  <span>Book en samtale om distribution</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative py-16 sm:py-24 bg-slate-900 text-white text-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/health-mobile-clinic-production.png", { width: 1600, quality: 80, format: 'auto' })}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-slate-950/50" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Skal vi styrke klinikkens rækkevidde og tilstrømning?
          </h2>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
            Book en uforpligtende samtale om, hvordan jeres videoer kan distribueres målrettet til jeres lokalområde.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold transition shadow-xl"
            >
              <span>Book en samtale</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
